import { CircleCheckIcon, CopyIcon } from "@cynthai/icons";
import React, { useCallback, useState } from "react";
import { tv } from "tailwind-variants";

import type { SlotClassNames } from "./types";

const copyButton = tv({
  slots: {
    container: "inline-flex cursor-pointer items-center",
    button: `
      ml-2 inline-flex cursor-pointer items-center justify-center rounded-md p-1
      transition-opacity duration-200
      hover:bg-gray-100
      dark:hover:bg-background/10
    `,
    icon: "size-5",
  },
  variants: {
    showOnParentHover: {
      true: {
        button: `
          opacity-0
          group-hover:opacity-100
        `,
      },
    },
    copied: {
      true: {
        icon: "text-green-600",
      },
      false: {
        icon: `
          text-gray-400 transition-colors duration-200
          hover:text-primary
        `,
      },
    },
  },
});

export interface CopyButtonProps {
  readonly text: string;
  readonly label: string;
  readonly classNames?: SlotClassNames<ReturnType<typeof copyButton>>;
  readonly onCopy?: () => void;
  readonly showOnParentHover?: boolean;
}

export function CopyButton({
  text,
  label,
  classNames,
  onCopy,
  showOnParentHover = false,
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      void navigator.clipboard.writeText(text);
      setCopied(true);
      onCopy?.();
      setTimeout(() => setCopied(false), 2000);
    },
    [text, onCopy],
  );

  const styles = copyButton({ showOnParentHover, copied });

  return (
    <div className={styles.container({ className: classNames?.container })}>
      <button
        type="button"
        onClick={handleCopy}
        title={`Copy ${label}`}
        className={styles.button({ className: classNames?.button })}
        aria-label={`Copy ${label}`}
      >
        {copied ? (
          <CircleCheckIcon
            className={styles.icon({ className: classNames?.icon })}
          />
        ) : (
          <CopyIcon className={styles.icon({ className: classNames?.icon })} />
        )}
      </button>
    </div>
  );
}
